/**
 * The reducer: a batch of ops applied to a studio draft, atomically.
 *
 * It works on a copy, applies the ops in order, and on any problem answers
 * every problem of the batch and leaves the input untouched; an op whose
 * target is missing records its problem and is skipped, and the ops after it
 * still run, so a batch is told everything at once. After the last op the
 * builder's plain mends run on every record the batch touched (silently: the
 * receipt shows what changed), and provenance and notes about a part the
 * batch took away are dropped; one about a part that never was is the
 * judge's to refuse.
 *
 * No judge runs here: the client calls this to draw a streamed batch before
 * the server answers, and `studioCommit` (commit.ts) adds the judge.
 */
import { LINES_MAX_DEPTH } from '../api';
import { isAgentLanguageTag } from '../agent-language';
import type { SetupDraftField } from '../compose-author';
import { isCurrencyCode } from '../currencies';
import {
  appMapProblem,
  STUDIO_LIMITS,
  type CriticCheck,
  type StudioBoardCard,
  type StudioDraft,
  type StudioFrom,
  type StudioImport,
  type StudioNote,
  type StudioNotYet,
  type StudioRecord,
  type StudioRole,
  type StudioTile,
} from './draft';
import { removeWithDependents, studioDependents, studioHasDependents, type StudioDependents } from './dependents';
import { mendStudioImports } from './draft-imports';
import { headlineFieldOf, mendDraftRecord } from './mend';
import { typedAccountRows } from './account-rows';
import { accountRowNames, accountsListOf, postingOf, ruleFollowsOptions, type MoneyRule } from './money-rules';
import type {
  StudioAddField,
  StudioAddRecord,
  StudioCascade,
  StudioFieldInput,
  StudioMoveField,
  StudioOp,
  StudioRemoveField,
  StudioRemoveRecord,
  StudioRename,
  StudioSeedList,
  StudioSetWorkspace,
  StudioTileInput,
  StudioUpdateField,
  StudioUpdateRecord,
} from './ops';
import { mintStudioKey, STUDIO_KEY, studioPartExists, studioPath, studioPathsMeet, type StudioKeyFallback } from './paths';

export type StudioOpProblem = { code: string; op: number | null; message: string; part?: string; dependents?: StudioDependents };

export type StudioApplied = { ok: true; draft: StudioDraft; touched: string[] } | { ok: false; problems: StudioOpProblem[] };

const NAME_MAX = 80;
const WORKSPACE_NAME_MAX = 120;
const PLACE_MAX = 120;
const DOES_MAX = 280;
const SITE_MAX = 300;

/** An `https` address with a host, at most `SITE_MAX` characters: the only shape a business's own site is kept in. */
const isSiteUrl = (raw: string): boolean => {
  if (raw.length === 0 || raw.length > SITE_MAX) return false;
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' && url.hostname.includes('.') && url.username === '' && url.password === '';
  } catch {
    return false;
  }
};

/** One op's run: the working draft, and where its problems and touched paths go. */
export type StudioOpRun = {
  draft: StudioDraft;
  index: number;
  touch: (path: string) => void;
  refuse: (code: string, message: string, part?: string, dependents?: StudioDependents) => void;
  /** A check that runs once the whole batch has, refusing as this op: what a later op of the batch may still put right. */
  defer: (check: () => void) => void;
};

/* ------------------------------------------------------------------ helpers */

/** Whether a given key keeps the key grammar (`STUDIO_KEY`), said as KEY_INVALID when it does not: a key is a segment of every part path. */
const keyOk = (run: StudioOpRun, key: string, part?: string): boolean => {
  if (STUDIO_KEY.test(key)) return true;
  run.refuse('KEY_INVALID', `"${key}" is not a key: lower case letters, digits and _, starting with a letter.`, part);
  return false;
};

/** Keys for a list of new things: a given key is kept (and refused if it breaks the grammar or is taken), a missing one is minted from its words. */
export const mintKeys = <T extends { key?: string }>(
  run: StudioOpRun,
  items: readonly T[],
  taken: Set<string>,
  wordsOf: (item: T) => string,
  fallback: StudioKeyFallback,
  takenCode: string,
  part?: string,
): Array<T & { key: string }> | null => {
  let ok = true;
  const out = items.map((item) => {
    if (item.key !== undefined) {
      if (!keyOk(run, item.key, part)) {
        ok = false;
        return { ...item, key: item.key };
      }
      if (taken.has(item.key)) {
        run.refuse(takenCode, `"${item.key}" is already a key here.`, part);
        ok = false;
      }
      taken.add(item.key);
      return { ...item, key: item.key };
    }
    const key = mintStudioKey(wordsOf(item), taken, fallback);
    taken.add(key);
    return { ...item, key };
  });
  return ok ? out : null;
};

/** Fields with keys, line fields too, each list minting against its own keys. */
const keyedFields = (run: StudioOpRun, inputs: readonly StudioFieldInput[], taken: Set<string>, part: string): SetupDraftField[] | null => {
  const keyed = mintKeys(run, inputs, taken, (field) => field.label, 'f', 'FIELD_KEY_TAKEN', part);
  if (keyed === null) return null;
  let ok = true;
  const out = keyed.map((field): SetupDraftField => {
    if (field.lines === undefined) return field as SetupDraftField;
    const lines = keyedFields(run, field.lines.fields, new Set(), `${part}/field:${field.key}`);
    if (lines === null) {
      ok = false;
      return field as SetupDraftField;
    }
    return { ...field, lines: { ...field.lines, fields: lines } };
  });
  return ok ? out : null;
};

/** Keeps where a part came from; a later op on the same part replaces it. */
export const keepFrom = (draft: StudioDraft, path: string, from: StudioFrom[] | undefined): void => {
  if (from === undefined || from.length === 0) return;
  const held = draft.from.find((entry) => entry.path === path);
  if (held === undefined) draft.from.push({ path, from });
  else held.from = from;
};

const recordOf = (run: StudioOpRun, key: string): StudioRecord | null => {
  const record = run.draft.records.find((one) => one.key === key);
  if (record === undefined) run.refuse('RECORD_UNKNOWN', `"${key}" is not a record or list of this draft.`, `record:${key}`);
  return record ?? null;
};

/** The field list a `within` chain lands in, with the problem said when it lands nowhere. */
const listAt = (run: StudioOpRun, at: { record: string; within?: string[] }): { record: StudioRecord; fields: SetupDraftField[] } | null => {
  const record = recordOf(run, at.record);
  if (record === null) return null;
  const within = at.within ?? [];
  if (within.length > LINES_MAX_DEPTH) {
    run.refuse('WITHIN_TOO_DEEP', `Lines go ${LINES_MAX_DEPTH} levels deep at most.`, `record:${at.record}`);
    return null;
  }
  let fields = record.fields;
  for (const key of within) {
    const lines = fields.find((field) => field.key === key);
    if (lines === undefined || lines.type !== 'lines' || lines.lines === undefined) {
      run.refuse('WITHIN_NOT_LINES', `"${key}" is not a field of lines on "${at.record}".`, `record:${at.record}`);
      return null;
    }
    fields = lines.lines.fields;
  }
  return { record, fields };
};

const pathOf = (at: { record: string; within?: string[] }, field: string): string =>
  studioPath({ kind: 'field', record: at.record, ...(at.within === undefined || at.within.length === 0 ? {} : { within: at.within }), field });

const nameOk = (run: StudioOpRun, name: string, part: string): string | null => {
  const trimmed = name.trim();
  if (trimmed.length === 0 || trimmed.length > NAME_MAX) {
    run.refuse('NAME_EMPTY', `A name is 1 to ${NAME_MAX} characters.`, part);
    return null;
  }
  return trimmed;
};

/** Inserts before `before` (a key in `list`), or at the end; false when `before` names nothing there. */
const insertBefore = <T extends { key: string }>(list: T[], item: T, before: string | undefined): boolean => {
  if (before === undefined) {
    list.push(item);
    return true;
  }
  const at = list.findIndex((one) => one.key === before);
  if (at < 0) return false;
  list.splice(at, 0, item);
  return true;
};

/**
 * What keeps a part from going: its dependents but the rows planned from a
 * file (an import into it, a list's seed rows), which go or are mended with
 * it (step 2.2 of docs/plans/setup-file-data.md), so a planned import never
 * holds a record the person leaves out.
 */
const holding = (dependents: StudioDependents): StudioDependents => ({
  goes: dependents.goes.filter((one) => !one.startsWith('import:')),
  stays: dependents.stays.filter((one) => !one.path.startsWith('import:') && !one.path.endsWith('/rows')),
});

/**
 * What keeps a part the person asked to take off from going (`with_serving`):
 * a field that would go with it (one that picks from it or is worked out from
 * it, on any record, its own too), and a money rule that reads it. What only
 * shows or serves it goes or is mended with it: its cards and Dashboard
 * cards, the notes about it, a binding, the layout, a saved view, a show rule
 * or filter, a role's access row, the rows planned from a file. Asked five
 * times to take Daily delivery off, OS said "other parts of the setup still
 * need it", when only the note that it was added for them did (dairy run, 30
 * Oct); Ghar kharcha's Category was held by its own card.
 */
const servingOnly = (dependents: StudioDependents): StudioDependents => ({
  goes: dependents.goes.filter((one) => one.includes('/field:')),
  stays: dependents.stays.filter((one) => one.because === 'posts_into'),
});

/** The leave-out of a record or field: refused with what holds it, or applied with every dependent. */
const leaveOut = (run: StudioOpRun, path: string, cascade: StudioCascade | undefined): void => {
  const dependents = studioDependents(run.draft, path);
  const held = cascade === 'with_serving' ? servingOnly(dependents) : holding(dependents);
  if (cascade !== 'with_dependents' && studioHasDependents(held)) {
    run.refuse('HAS_DEPENDENTS', 'Other parts depend on this one. Leave them out too, or change them first.', path, held);
    return;
  }
  const done = removeWithDependents(run.draft, path);
  run.touch(path);
  for (const one of done.goes) run.touch(one);
  for (const one of done.stays) run.touch(one.path);
};

/* ------------------------------------------------------------- the handlers */

const setWorkspace = (run: StudioOpRun, op: StudioSetWorkspace): void => {
  const meta = run.draft.meta;
  let ok = true;
  const no = (code: string, message: string): void => {
    run.refuse(code, message, 'meta');
    ok = false;
  };
  if (op.currency !== undefined && !isCurrencyCode(op.currency)) no('CURRENCY_UNKNOWN', `"${op.currency}" is not an active ISO 4217 currency.`);
  if (op.language !== undefined && !isAgentLanguageTag(op.language)) no('LANGUAGE_UNKNOWN', `"${String(op.language)}" is not a language OS speaks.`);
  if (op.name !== undefined && op.name.trim().length > WORKSPACE_NAME_MAX) no('NAME_TOO_LONG', `A workspace's name is ${WORKSPACE_NAME_MAX} characters at most.`);
  if (op.place !== undefined && op.place.trim().length > PLACE_MAX) no('TEXT_TOO_LONG', `A place is ${PLACE_MAX} characters at most.`);
  if (op.does !== undefined && op.does.trim().length > DOES_MAX) no('TEXT_TOO_LONG', `What the business does is ${DOES_MAX} characters at most.`);
  if (op.site !== undefined && !isSiteUrl(op.site.trim())) no('SITE_NOT_URL', `A site is an https address of ${SITE_MAX} characters at most.`);
  if (!ok) return;
  if (op.name !== undefined) meta.name = op.name.trim();
  if (op.currency !== undefined) meta.currency = op.currency.trim().toUpperCase();
  if (op.language !== undefined) meta.language = op.language;
  if (op.place !== undefined) meta.place = op.place.trim();
  if (op.does !== undefined) meta.does = op.does.trim();
  if (op.site !== undefined) meta.site = op.site.trim();
  for (const member of op.clear ?? []) delete meta[member];
  run.touch('meta');
};

const addRecord = (run: StudioOpRun, op: StudioAddRecord): void => {
  const draft = run.draft;
  const taken = new Set(draft.records.map((one) => one.key));
  if (op.key !== undefined && !keyOk(run, op.key)) return;
  if (op.key !== undefined && taken.has(op.key)) {
    run.refuse('RECORD_KEY_TAKEN', `"${op.key}" is already a record's key.`, `record:${op.key}`);
    return;
  }
  if (op.before !== undefined && !taken.has(op.before)) {
    run.refuse('BEFORE_UNKNOWN', `"${op.before}" is not a record to go before.`);
    return;
  }
  const name = nameOk(run, op.name, 'meta');
  if (name === null) return;
  const key = op.key ?? mintStudioKey(name, taken, 'r');
  const part = `record:${key}`;
  const fields = keyedFields(run, op.fields, new Set(), part);
  if (fields === null) return;
  const tiles = op.tiles === undefined ? undefined : mintKeys(run, op.tiles, new Set(), (tile: StudioTileInput) => tile.label, 'c', 'CARD_KEY_TAKEN', part);
  if (tiles === null) return;
  const record: StudioRecord = {
    key,
    name,
    entryNoun: op.entryNoun ?? name.toLowerCase(),
    kind: op.kind,
    direction: op.direction ?? 'none',
    fields,
    headlineFieldKey: op.headlineFieldKey ?? headlineFieldOf(fields, fields[0]?.key ?? ''),
    ...(op.icon === undefined ? {} : { icon: op.icon }),
    ...(op.dateFieldKey === undefined ? {} : { dateFieldKey: op.dateFieldKey }),
    ...(op.measureFieldKey === undefined ? {} : { measureFieldKey: op.measureFieldKey }),
    ...(op.signFieldKey === undefined ? {} : { signFieldKey: op.signFieldKey }),
    ...(op.focusFieldKey === undefined ? {} : { focusFieldKey: op.focusFieldKey }),
    ...(op.capture === undefined ? {} : { capture: op.capture }),
    ...(op.layout === undefined ? {} : { layout: op.layout }),
    ...(op.seedRows === undefined ? {} : { seedRows: op.seedRows }),
    ...(tiles === undefined ? {} : { tiles: tiles as StudioTile[] }),
    ...(op.rules === undefined || op.rules.length === 0 ? {} : { rules: op.rules }),
    ...(op.views === undefined || op.views.length === 0 ? {} : { views: op.views }),
    ...(op.posting === undefined ? {} : { posting: op.posting }),
    ...(op.accounts === undefined ? {} : { accounts: op.accounts }),
    ...(op.stockItems === undefined ? {} : { stockItems: op.stockItems }),
    ...(op.chargePlan === undefined ? {} : { chargePlan: op.chargePlan }),
    ...(op.approvalChain === undefined ? {} : { approvalChain: op.approvalChain }),
    ...(op.tasks === undefined || op.tasks.length === 0 ? {} : { tasks: op.tasks }),
  };
  insertBefore(draft.records, record, op.before);
  keepFrom(draft, part, op.from);
  run.touch(part);
};

const updateRecord = (run: StudioOpRun, op: StudioUpdateRecord): void => {
  const record = recordOf(run, op.record);
  if (record === null) return;
  const { op: _op, record: _key, clear, ...set } = op;
  Object.assign(record, set);
  for (const member of clear ?? []) delete record[member];
  run.touch(`record:${op.record}/props`);
};

/** Rename's targets: a record, a field, and (below) a card, a Dashboard card and a role. */
export const RENAMERS: Partial<Record<StudioRename['target']['kind'], (run: StudioOpRun, op: StudioRename, name: string) => void>> = {
  record: (run, op, name) => {
    if (op.target.kind !== 'record') return;
    const record = recordOf(run, op.target.record);
    if (record === null) return;
    // Two records of one name are never told apart ("Gwale or Gwale", S4, 30 Oct): a name another holds is refused.
    const fold = (words: string): string => words.trim().toLowerCase().replace(/\s+/g, ' ');
    if (run.draft.records.some((one) => one.key !== record.key && fold(one.name) === fold(name))) {
      run.refuse('RECORD_NAME_TAKEN', `"${name}" is already the name of another record or list.`, `record:${op.target.record}`);
      return;
    }
    record.name = name;
    run.touch(`record:${op.target.record}/props`);
  },
  field: (run, op, name) => {
    if (op.target.kind !== 'field') return;
    const target = op.target;
    const list = listAt(run, target);
    if (list === null) return;
    const field = list.fields.find((one) => one.key === target.field);
    if (field === undefined) {
      run.refuse('FIELD_UNKNOWN', `"${target.field}" is not a field there.`, pathOf(target, target.field));
      return;
    }
    field.label = name;
    run.touch(pathOf(target, target.field));
  },
};

const rename = (run: StudioOpRun, op: StudioRename): void => {
  const renamer = RENAMERS[op.target.kind];
  if (renamer === undefined) {
    run.refuse('RENAME_TARGET', `A ${op.target.kind} is not renamed.`);
    return;
  }
  const name = nameOk(run, op.name, studioPath(op.target));
  if (name === null) return;
  renamer(run, op, name);
};

const removeRecord = (run: StudioOpRun, op: StudioRemoveRecord): void => {
  if (recordOf(run, op.record) === null) return;
  leaveOut(run, `record:${op.record}`, op.cascade);
};

const addField = (run: StudioOpRun, op: StudioAddField): void => {
  const list = listAt(run, op);
  if (list === null) return;
  const taken = new Set(list.fields.map((one) => one.key));
  const part = studioPath({ kind: 'record', record: op.record });
  if (op.before !== undefined && !taken.has(op.before)) {
    run.refuse('BEFORE_UNKNOWN', `"${op.before}" is not a field to go before.`, part);
    return;
  }
  const keyed = keyedFields(run, [op.field], taken, part);
  if (keyed === null) return;
  const field = keyed[0]!;
  insertBefore(list.fields, field, op.before);
  const path = pathOf(op, field.key);
  keepFrom(run.draft, path, op.from);
  run.touch(path);
};

const fieldAt = (run: StudioOpRun, at: { record: string; within?: string[] }, key: string): { fields: SetupDraftField[]; field: SetupDraftField } | null => {
  const list = listAt(run, at);
  if (list === null) return null;
  const field = list.fields.find((one) => one.key === key);
  if (field === undefined) {
    run.refuse('FIELD_UNKNOWN', `"${key}" is not a field there.`, pathOf(at, key));
    return null;
  }
  return { fields: list.fields, field };
};

const updateField = (run: StudioOpRun, op: StudioUpdateField): void => {
  const found = fieldAt(run, op, op.field);
  if (found === null) return;
  const { field } = found;
  const { lines, ...set } = op.set;
  Object.assign(field, set);
  // A choice the record's money rule reads keeps its rule's map of each option to an account (S12).
  const rule = op.within === undefined && set.options !== undefined ? run.draft.records.find((one) => one.key === op.record) : undefined;
  const posting = rule === undefined ? null : postingOf(rule);
  if (rule !== undefined && posting !== null) {
    const accounts = accountsListOf(run.draft);
    const followed = ruleFollowsOptions(posting, field, rule.direction === 'out' ? 'out' : 'in', accounts === null ? new Set() : accountRowNames(accounts));
    if (followed !== posting) (rule as { posting?: MoneyRule }).posting = followed;
  }
  if (lines !== undefined) {
    field.lines = {
      noun: lines.noun ?? field.lines?.noun ?? '',
      nouns: lines.nouns ?? field.lines?.nouns ?? '',
      fields: field.lines?.fields ?? [],
      ...((lines.countFrom ?? field.lines?.countFrom) === undefined ? {} : { countFrom: lines.countFrom ?? field.lines?.countFrom }),
      ...((lines.max ?? field.lines?.max) === undefined ? {} : { max: lines.max ?? field.lines?.max }),
      ...((lines.start ?? field.lines?.start) === undefined ? {} : { start: lines.start ?? field.lines?.start }),
    };
  }
  for (const member of op.clear ?? []) {
    delete field[member];
    // Fill once says something about the default, and goes with it.
    if (member === 'startsWith') delete field.startsOnce;
  }
  run.touch(pathOf(op, op.field));
};

const removeField = (run: StudioOpRun, op: StudioRemoveField): void => {
  if (fieldAt(run, op, op.field) === null) return;
  leaveOut(run, pathOf(op, op.field), op.cascade);
};

const moveField = (run: StudioOpRun, op: StudioMoveField): void => {
  const found = fieldAt(run, op, op.field);
  if (found === null) return;
  const { fields, field } = found;
  if (op.before !== undefined && (op.before === op.field || !fields.some((one) => one.key === op.before))) {
    run.refuse('BEFORE_UNKNOWN', `"${op.before}" is not a field to go before.`, `record:${op.record}`);
    return;
  }
  fields.splice(fields.indexOf(field), 1);
  insertBefore(fields, field, op.before);
  run.touch(`record:${op.record}/order`);
};

const seedList = (run: StudioOpRun, op: StudioSeedList): void => {
  const record = recordOf(run, op.record);
  if (record === null) return;
  if (record.kind !== 'directory') {
    run.refuse('NOT_A_LIST', `"${op.record}" is a record. Only a list starts with rows.`, `record:${op.record}`);
    return;
  }
  const rows = op.mode === 'replace' ? op.rows : [...(record.seedRows ?? []), ...op.rows];
  if (rows.length > STUDIO_LIMITS.seedRows) {
    run.refuse('LIMIT', `A list starts with ${STUDIO_LIMITS.seedRows} rows at most.`, `record:${op.record}`);
    return;
  }
  record.seedRows = rows;
  run.touch(`record:${op.record}/rows`);
};

/* ---------------------------------------------- cards, roles, imports, notes */

type OpOf<K extends StudioOp['op']> = Extract<StudioOp, { op: K }>;

/** A keyed part list of the draft, and the words its keys are minted from. */
type Keyed<T extends { key: string }> = {
  list: (draft: StudioDraft) => T[];
  set: (draft: StudioDraft, list: T[]) => void;
  path: (key: string) => string;
  unknown: string;
  taken: string;
  fallback: StudioKeyFallback;
};

const BOARDS: Keyed<StudioBoardCard> = { list: (d) => d.boardCards, set: (d, l) => (d.boardCards = l), path: (k) => `board:${k}`, unknown: 'BOARD_UNKNOWN', taken: 'BOARD_KEY_TAKEN', fallback: 'b' };
const ROLES: Keyed<StudioRole> = { list: (d) => d.roles, set: (d, l) => (d.roles = l), path: (k) => `role:${k}`, unknown: 'ROLE_UNKNOWN', taken: 'ROLE_KEY_TAKEN', fallback: 'role' };
const IMPORTS: Keyed<StudioImport> = { list: (d) => d.imports, set: (d, l) => (d.imports = l), path: (k) => `import:${k}`, unknown: 'IMPORT_UNKNOWN', taken: 'IMPORT_KEY_TAKEN', fallback: 'i' };
const NOTES: Keyed<StudioNote> = { list: (d) => d.notes, set: (d, l) => (d.notes = l), path: (k) => `note:${k}`, unknown: 'NOTE_UNKNOWN', taken: 'NOTE_KEY_TAKEN', fallback: 'n' };
const NOT_YET: Keyed<StudioNotYet> = { list: (d) => d.notYet, set: (d, l) => (d.notYet = l), path: (k) => `not_yet:${k}`, unknown: 'NOT_YET_UNKNOWN', taken: 'NOT_YET_KEY_TAKEN', fallback: 'x' };

/** Adds one keyed part, minting its key from `words` when none is given. */
const addKeyed = <T extends { key: string }>(run: StudioOpRun, kind: Keyed<T>, item: Omit<T, 'key'> & { key?: string }, words: string, from?: StudioFrom[]): void => {
  const list = kind.list(run.draft);
  const taken = new Set(list.map((one) => one.key));
  if (item.key !== undefined && !keyOk(run, item.key)) return;
  if (item.key !== undefined && taken.has(item.key)) {
    run.refuse(kind.taken, `"${item.key}" is already a key here.`, kind.path(item.key));
    return;
  }
  const key = item.key ?? mintStudioKey(words, taken, kind.fallback);
  list.push({ ...item, key } as T);
  keepFrom(run.draft, kind.path(key), from);
  run.touch(kind.path(key));
};

/** pg_trgm's trigrams of a text: lower-cased, split on anything not a letter or digit, each word padded. */
const trigramsOf = (text: string): Set<string> => {
  const found = new Set<string>();
  for (const word of text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((one) => one !== '')) {
    const padded = `  ${word} `;
    for (let at = 0; at + 3 <= padded.length; at += 1) found.add(padded.slice(at, at + 3));
  }
  return found;
};

/** pg_trgm's `similarity`. */
const trigramSimilarity = (a: string, b: string): number => {
  const left = trigramsOf(a);
  const right = trigramsOf(b);
  if (left.size === 0 || right.size === 0) return 0;
  let shared = 0;
  for (const one of left) if (right.has(one)) shared += 1;
  return shared / (left.size + right.size - shared);
};

/**
 * A wish already noted, by its manifest entry or its words, is never noted
 * twice (3.13). Asked for again, it is answered, never refused (16 Oct): the
 * draft keeps the one it holds and the op changes nothing, so the turn that
 * asked says again that it is not possible yet, with its closest fit.
 */
const SAME_WISH = 0.7;

const addNotYet = (run: StudioOpRun, op: Extract<StudioOp, { op: 'not_yet' }>): void => {
  const item = op.item;
  // A key already taken is the wire's mistake, said as such.
  if (item.key !== undefined && run.draft.notYet.some((one) => one.key === item.key)) {
    addKeyed(run, NOT_YET, item, item.name, undefined);
    return;
  }
  const same = run.draft.notYet.find(
    (one) => (item.capability !== undefined && one.capability === item.capability) || trigramSimilarity(one.need, item.need) >= SAME_WISH,
  );
  if (same !== undefined) return;
  addKeyed(run, NOT_YET, item, item.name, undefined);
};

const findKeyed = <T extends { key: string }>(run: StudioOpRun, kind: Keyed<T>, key: string): T | null => {
  const found = kind.list(run.draft).find((one) => one.key === key);
  if (found === undefined) run.refuse(kind.unknown, `"${key}" is not there.`, kind.path(key));
  return found ?? null;
};

const removeKeyed = <T extends { key: string }>(run: StudioOpRun, kind: Keyed<T>, key: string): void => {
  if (findKeyed(run, kind, key) === null) return;
  kind.set(run.draft, kind.list(run.draft).filter((one) => one.key !== key));
  run.touch(kind.path(key));
};

const addCard = (run: StudioOpRun, op: OpOf<'add_card'>): void => {
  const record = recordOf(run, op.record);
  if (record === null) return;
  const part = `record:${op.record}`;
  if (record.kind === 'directory') {
    run.refuse('CARD_ON_LIST', `"${op.record}" is a list. Cards are pinned on records.`, part);
    return;
  }
  const tiles = record.tiles ?? [];
  if (tiles.length >= STUDIO_LIMITS.tilesPerRecord) {
    run.refuse('LIMIT', `A record pins ${STUDIO_LIMITS.tilesPerRecord} cards at most.`, part);
    return;
  }
  const keyed = mintKeys(run, [op.card], new Set(tiles.map((one) => one.key)), (tile) => tile.label, 'c', 'CARD_KEY_TAKEN', part);
  if (keyed === null) return;
  record.tiles = [...tiles, keyed[0] as StudioTile];
  const path = `${part}/card:${keyed[0]!.key}`;
  keepFrom(run.draft, path, op.from);
  run.touch(path);
};

const removeCard = (run: StudioOpRun, op: OpOf<'remove_card'>): void => {
  const record = recordOf(run, op.record);
  if (record === null) return;
  const path = `record:${op.record}/card:${op.card}`;
  if (!(record.tiles ?? []).some((tile) => tile.key === op.card)) {
    run.refuse('CARD_UNKNOWN', `"${op.card}" is not a card of "${op.record}".`, path);
    return;
  }
  record.tiles = record.tiles!.filter((tile) => tile.key !== op.card);
  run.touch(path);
};

const updateRole = (run: StudioOpRun, op: OpOf<'update_role'>): void => {
  const role = findKeyed(run, ROLES, op.role);
  if (role === null) return;
  if (op.purpose !== undefined) role.purpose = op.purpose;
  if (op.people !== undefined) role.people = op.people;
  if (op.base !== undefined) role.base = op.base;
  if (op.permissions !== undefined) role.permissions = op.permissions;
  if (op.access !== undefined) role.access = op.access;
  if (op.clear?.includes('purpose') === true) delete role.purpose;
  run.touch(ROLES.path(op.role));
};

const mapImport = (run: StudioOpRun, op: OpOf<'map_import'>): void => {
  const plan = op.import;
  // One import for each sheet and kind of its rows (W8 of docs/plans/workbook-map.md): the same kind into another record
  // moves it. A sheet of one kind keeps one import for each record.
  const kind = plan.rowKind?.kind;
  const same = run.draft.imports.find(
    (one) => one.source === plan.source && one.sheet === plan.sheet && (kind === undefined ? one.rowKind === undefined && one.record === plan.record : one.rowKind?.kind === kind),
  );
  if (same === undefined) {
    addKeyed(run, IMPORTS, plan, plan.rowKind?.name === undefined ? plan.sheet : `${plan.sheet} ${plan.rowKind.name}`);
    return;
  }
  // The same sheet into the same record is the same import: replaced in place, its key kept.
  Object.assign(same, { ...plan, key: same.key });
  if (plan.rows === undefined) delete same.rows;
  if (plan.rowKind === undefined) delete same.rowKind;
  run.touch(IMPORTS.path(same.key));
};

const setSource = (run: StudioOpRun, op: OpOf<'set_source'>): void => {
  const sources = run.draft.sources;
  const held = sources.find((one) => one.id === op.source);
  const dismissed = op.dismissed ?? held?.dismissed ?? false;
  if (held === undefined) sources.push({ id: op.source, role: op.role, dismissed });
  else Object.assign(held, { role: op.role, dismissed });
  run.touch(`source:${op.source}`);
  if (!dismissed) return;
  // Unless a later op of the same batch takes the import away.
  run.defer(() => {
    const reading = run.draft.imports.filter((one) => one.source === op.source);
    if (reading.length > 0) {
      run.refuse('IMPORT_SOURCE_DISMISSED', 'An import still reads this source. Take the import away, or keep the source.', `import:${reading[0]!.key}`);
    }
  });
};

const updateNote = (run: StudioOpRun, op: OpOf<'update_note'>): void => {
  const note = findKeyed(run, NOTES, op.note);
  if (note === null) return;
  if (op.card !== undefined) note.card = op.card;
  if (op.clear?.includes('card') === true) delete note.card;
  if (op.sources !== undefined) note.sources = [...op.sources];
  run.touch(NOTES.path(op.note));
};

/** Where a part came from, said whole; an empty list forgets it. The part must be in the draft. */
const setFrom = (run: StudioOpRun, op: OpOf<'set_from'>): void => {
  if (!studioPartExists(run.draft, op.path)) {
    run.refuse('FROM_PART_UNKNOWN', `"${op.path}" is not a part of this draft.`, op.path);
    return;
  }
  if (op.from.length > STUDIO_LIMITS.fromPerPart) {
    run.refuse('FROM_TOO_MANY', `A part keeps ${STUDIO_LIMITS.fromPerPart} origins at most.`, op.path);
    return;
  }
  if (op.from.length === 0) run.draft.from = run.draft.from.filter((entry) => entry.path !== op.path);
  else keepFrom(run.draft, op.path, op.from);
  run.touch(op.path);
};

const CHECK_GAP = 'critic:';

/** A critic's check's words, kept on the gap it left: what it asked outlives the check. */
const checkWordsOf = (check: CriticCheck | undefined): { title?: string; question?: string } =>
  check === undefined ? {} : { title: check.title, question: check.question };

const dismissGap = (run: StudioOpRun, op: OpOf<'dismiss_gap'>): void => {
  if (run.draft.dismissedGaps.some((one) => one.gap === op.gap)) {
    run.refuse('GAP_TWICE', `"${op.gap}" is already left.`, `gap:${op.gap}`);
    return;
  }
  const check = op.gap.startsWith(CHECK_GAP) ? (run.draft.checks ?? []).find((one) => `${CHECK_GAP}${one.id}` === op.gap) : undefined;
  run.draft.dismissedGaps.push({ gap: op.gap, ...(op.reason === undefined ? {} : { reason: op.reason }), ...checkWordsOf(check) });
  run.touch(`gap:${op.gap}`);
};

const addCheck = (run: StudioOpRun, op: OpOf<'add_check'>): void => {
  const checks = run.draft.checks ?? [];
  const path = `check:${op.check.id}`;
  // The id is a gap id (`critic:<id>`) the harness writes into OS's instruction: it keeps the key grammar.
  if (!keyOk(run, op.check.id, path)) return;
  if (checks.some((one) => one.id === op.check.id)) {
    run.refuse('CHECK_TWICE', `"${op.check.id}" is already a check.`, path);
    return;
  }
  if (checks.length >= STUDIO_LIMITS.checks) {
    run.refuse('LIMIT', `A draft holds ${STUDIO_LIMITS.checks} checks at most.`, path);
    return;
  }
  run.draft.checks = [...checks, op.check];
  run.touch(path);
};

const dropCheck = (run: StudioOpRun, op: OpOf<'drop_check'>): void => {
  const checks = run.draft.checks ?? [];
  const path = `check:${op.id}`;
  if (!checks.some((one) => one.id === op.id)) {
    run.refuse('CHECK_UNKNOWN', `"${op.id}" is not a check.`, path);
    return;
  }
  // A check left before it is taken off keeps its words on the gap it left, however the batch was ordered before.
  const gap = `${CHECK_GAP}${op.id}`;
  const left = run.draft.dismissedGaps.findIndex((one) => one.gap === gap && one.title === undefined);
  if (left >= 0) run.draft.dismissedGaps[left] = { ...run.draft.dismissedGaps[left]!, ...checkWordsOf(checks.find((one) => one.id === op.id)) };
  run.draft.checks = checks.filter((one) => one.id !== op.id);
  run.touch(path);
};

const reopenGap = (run: StudioOpRun, op: OpOf<'reopen_gap'>): void => {
  const gaps = run.draft.dismissedGaps;
  if (!gaps.some((one) => one.gap === op.gap)) {
    run.refuse('GAP_NOT_DISMISSED', `"${op.gap}" was never left.`, `gap:${op.gap}`);
    return;
  }
  run.draft.dismissedGaps = gaps.filter((one) => one.gap !== op.gap);
  run.touch(`gap:${op.gap}`);
};

/** A conflict's gap id, as the gap engine raises it (`source_conflict`). */
const conflictGap = (conflictId: string): string => `source_conflict:${conflictId}`;

/** The side the person took (9.10): the conflict's gap is left, as answered, and its Decide later is cleared. */
const pickConflict = (run: StudioOpRun, op: OpOf<'pick_conflict'>): void => {
  const gap = conflictGap(op.conflictId);
  if (run.draft.dismissedGaps.some((one) => one.gap === gap)) {
    run.refuse('GAP_TWICE', `"${gap}" is already answered.`, `gap:${gap}`);
    return;
  }
  run.draft.dismissedGaps.push({ gap, reason: `picked ${op.sourceId}` });
  const later = (run.draft.laterConflicts ?? []).filter((one) => one !== op.conflictId);
  if (later.length > 0) run.draft.laterConflicts = later;
  else delete run.draft.laterConflicts;
  run.touch(`gap:${gap}`);
};

/** Decide later (9.10): the gap stays open and is marked; only once. */
const laterConflict = (run: StudioOpRun, op: OpOf<'later_conflict'>): void => {
  const gap = conflictGap(op.conflictId);
  const later = run.draft.laterConflicts ?? [];
  if (later.includes(op.conflictId)) {
    run.refuse('CONFLICT_LATER_TWICE', `"${gap}" is already left for later.`, `gap:${gap}`);
    return;
  }
  if (run.draft.dismissedGaps.some((one) => one.gap === gap)) {
    run.refuse('GAP_TWICE', `"${gap}" is already answered.`, `gap:${gap}`);
    return;
  }
  run.draft.laterConflicts = [...later, op.conflictId];
  run.touch(`gap:${gap}`);
};

/** The map from the app the person uses (6.14): one per id, replaced whole; its shape is judged once the batch is in. */
const setAppMap = (run: StudioOpRun, op: OpOf<'set_app_map'>): void => {
  const path = `app_map:${op.map.id}`;
  if (!keyOk(run, op.map.id, path)) return;
  const maps = run.draft.appMaps ?? [];
  const at = maps.findIndex((one) => one.id === op.map.id);
  if (at === -1 && maps.length >= STUDIO_LIMITS.appMaps) {
    run.refuse('LIMIT', `A draft holds ${STUDIO_LIMITS.appMaps} app maps at most.`, path);
    return;
  }
  const map = structuredClone(op.map);
  run.draft.appMaps = at === -1 ? [...maps, map] : maps.map((one, index) => (index === at ? map : one));
  // A not-yet item the same batch notes is in the draft by the end of it.
  run.defer(() => {
    const problem = appMapProblem(run.draft, map);
    if (problem !== null) run.refuse('APP_MAP_SHAPE', problem, path);
  });
  run.touch(path);
};

const removeAppMap = (run: StudioOpRun, op: OpOf<'remove_app_map'>): void => {
  const maps = run.draft.appMaps ?? [];
  const path = `app_map:${op.id}`;
  if (!maps.some((one) => one.id === op.id)) {
    run.refuse('APP_MAP_UNKNOWN', `"${op.id}" is not an app map.`, path);
    return;
  }
  run.draft.appMaps = maps.filter((one) => one.id !== op.id);
  run.touch(path);
};

/** The words of a keyed part, for rename's card, Dashboard card and role targets. */
const renameKeyed = <T extends { key: string }>(kind: Keyed<T>, set: (item: T, name: string) => void) =>
  (run: StudioOpRun, op: StudioRename, name: string): void => {
    if (!('key' in op.target)) return;
    const found = findKeyed(run, kind, op.target.key);
    if (found === null) return;
    set(found, name);
    run.touch(kind.path(op.target.key));
  };

RENAMERS.board = renameKeyed(BOARDS, (card, name) => (card.label = name));
RENAMERS.role = renameKeyed(ROLES, (role, name) => (role.name = name));
RENAMERS.card = (run, op, name) => {
  if (op.target.kind !== 'card') return;
  const { record: key, card } = op.target;
  const record = recordOf(run, key);
  if (record === null) return;
  const tile = (record.tiles ?? []).find((one) => one.key === card);
  const path = `record:${key}/card:${card}`;
  if (tile === undefined) {
    run.refuse('CARD_UNKNOWN', `"${card}" is not a card of "${key}".`, path);
    return;
  }
  tile.label = name;
  run.touch(path);
};

/** Every op's handler, by name. */
export const STUDIO_OP_HANDLERS: { [K in StudioOp['op']]: (run: StudioOpRun, op: Extract<StudioOp, { op: K }>) => void } = {
  add_card: addCard,
  remove_card: removeCard,
  add_board_card: (run, op) => addKeyed(run, BOARDS, op.card, op.card.label, op.from),
  remove_board_card: (run, op) => removeKeyed(run, BOARDS, op.card),
  add_role: (run, op) => addKeyed(run, ROLES, op.role, op.role.name, op.from),
  update_role: updateRole,
  remove_role: (run, op) => removeKeyed(run, ROLES, op.role),
  map_import: mapImport,
  remove_import: (run, op) => removeKeyed(run, IMPORTS, op.import),
  set_source: setSource,
  set_from: setFrom,
  note: (run, op) => addKeyed(run, NOTES, op.note, op.note.text, undefined),
  remove_note: (run, op) => removeKeyed(run, NOTES, op.note),
  update_note: updateNote,
  not_yet: addNotYet,
  remove_not_yet: (run, op) => removeKeyed(run, NOT_YET, op.item),
  dismiss_gap: dismissGap,
  add_check: addCheck,
  drop_check: dropCheck,
  reopen_gap: reopenGap,
  pick_conflict: pickConflict,
  later_conflict: laterConflict,
  set_app_map: setAppMap,
  remove_app_map: removeAppMap,
  set_workspace: setWorkspace,
  add_record: addRecord,
  update_record: updateRecord,
  rename,
  remove_record: removeRecord,
  add_field: addField,
  update_field: updateField,
  remove_field: removeField,
  move_field: moveField,
  seed_list: seedList,
};

/* -------------------------------------------------------------- the reducer */

export const applyStudioOps = (draft: StudioDraft, ops: readonly StudioOp[]): StudioApplied => {
  if (ops.length > STUDIO_LIMITS.opsPerBatch) {
    return {
      ok: false,
      problems: [{ code: 'OPS_TOO_MANY', op: null, message: `A batch holds ${STUDIO_LIMITS.opsPerBatch} ops at most; this one holds ${ops.length}.` }],
    };
  }
  const work = structuredClone(draft);
  const touched = new Set<string>();
  const problems: StudioOpProblem[] = [];
  const deferred: Array<() => void> = [];
  ops.forEach((op, index) => {
    const run: StudioOpRun = {
      draft: work,
      index,
      touch: (path) => touched.add(path),
      refuse: (code, message, part, dependents) =>
        problems.push({ code, op: index, message, ...(part === undefined ? {} : { part }), ...(dependents === undefined ? {} : { dependents }) }),
      defer: (check) => deferred.push(check),
    };
    const handler = Object.hasOwn(STUDIO_OP_HANDLERS, op.op)
      ? (STUDIO_OP_HANDLERS as Partial<Record<string, (run: StudioOpRun, op: StudioOp) => void>>)[op.op]
      : undefined;
    if (handler === undefined) run.refuse('OP_UNKNOWN', `"${String(op.op)}" is not an op.`);
    else handler(run, op);
  });
  for (const check of deferred) check();
  if (problems.length > 0) return { ok: false, problems };

  // The builder's mends, on every record the batch reached.
  const paths = [...touched];
  work.records = work.records.map((record) =>
    paths.some((path) => studioPathsMeet(path, `record:${record.key}`)) ? mendDraftRecord(record) : record,
  );
  // The Accounts list's rows the batch reached, each with its type where it is clear: Build makes no account without one.
  const accounts = accountsListOf(work);
  if (accounts !== null && paths.some((path) => studioPathsMeet(path, `record:${accounts.key}`))) {
    const typed = typedAccountRows(work);
    if (typed !== null) accounts.seedRows = typed;
  }
  // Imports into the records the batch reached are kept in step with their fields.
  for (const one of mendStudioImports(work, (key) => paths.some((path) => studioPathsMeet(path, `record:${key}`)))) paths.push(one);
  // Where a part came from, and notes about it, go with the part: a part the
  // batch took away (one the draft held, or one the batch made and took away
  // again). An entry about a part that never was stays, for the judge to
  // refuse (NOTE_ABOUT_UNKNOWN, FROM_PART_UNKNOWN).
  const keeps = (path: string): boolean =>
    studioPartExists(work, path) || !(studioPartExists(draft, path) || paths.some((one) => studioPathsMeet(one, path)));
  work.from = work.from.filter((entry) => keeps(entry.path));
  work.notes = work.notes.filter((note) => note.about === 'workspace' || keeps(note.about));
  // A note answered by a Dashboard card the batch took away is unanswered again.
  for (const note of work.notes) {
    if (note.card !== undefined && !work.boardCards.some((card) => card.key === note.card) && draft.boardCards.some((card) => card.key === note.card)) delete note.card;
  }

  return { ok: true, draft: work, touched: paths.sort() };
};
